import { getAdminOrgId } from "./admin";
import { getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type ProcessTemplateStatus = "draft" | "published" | "archived";
export type CompletionPolicy = "all_required" | "manual_by_assignee" | "external_confirmation" | "time_based";
export type ActivityType = 
  // Communication
  | "call_client" | "send_email_manual" | "meeting" | "client_confirmation"
  // Administratif
  | "validate_document" | "upload_file" | "approve_internal" | "fill_form"
  // Opérationnel
  | "process_data" | "execute_service" | "quality_check" | "add_operational_cost"
  // Suivi
  | "schedule_followup" | "create_note"
  // Assignation
  | "assign_responsible"
  // Système
  | "system_validation"
  // Legacy
  | "call" | "email" | "upload" | "analysis" | "other";
export type TriggerEventType = "task_created" | "step_started" | "activity_completed" | "step_completed" | "step_overdue" | "task_overdue" | "manual";
export type TriggerActionType = 
  // Alertes
  | "create_alert" | "schedule_reminder"
  // Emails
  | "send_email"
  // Assignation
  | "reassign_task" | "reassign_step"
  // États
  | "set_task_health" | "set_task_status"
  // Automatisation
  | "create_subtask" | "pause_task" | "complete_task"
  // Legacy
  | "set_health_state";

export type ProcessTemplate = {
  id: string;
  org_id: string;
  name: string;
  ops_family: string | null;
  status: ProcessTemplateStatus;
  current_version_id: string | null;
  current_draft_version_id: string | null;
  current_published_version_id: string | null;
  draft_snapshot_json: any | null;
  created_at: string;
  updated_at: string;
};

export type ProcessTemplateCreate = {
  name: string;
  ops_family?: string | null;
};

export type ProcessTemplateUpdate = {
  name?: string;
  ops_family?: string | null;
  status?: ProcessTemplateStatus;
};

export type ProcessVersion = {
  id: string;
  org_id: string;
  process_template_id: string;
  version_number: number;
  published_at: string | null;
  snapshot_json: any | null;
  schema_version: string | null;
  snapshot_hash: string | null;
  created_at: string;
  updated_at: string;
};

export type StepConditionRule = {
  type: string;
  op: string;
  value: string | number | boolean;
  deliverable_code?: string | null;
  step_id?: string | null;
  activity_id?: string | null;
};

export type StepConditions = {
  mode: "structured" | "advanced";
  all: StepConditionRule[];
  any: StepConditionRule[];
  advanced?: any | null;
};

export type ProcessStepTemplate = {
  id: string;
  process_version_id: string;
  order_index: number;
  title: string;
  description: string | null;
  sla_days: number | null;
  completion_policy: CompletionPolicy;
  entry_conditions: any | null;
  exit_conditions: any | null;
  created_at: string;
  updated_at: string;
};

export type ProcessStepTemplateCreate = {
  order_index: number;
  title: string;
  description?: string | null;
  sla_days?: number | null;
  completion_policy?: CompletionPolicy;
  entry_conditions?: any | null;
  exit_conditions?: any | null;
};

export type ProcessStepTemplateUpdate = {
  order_index?: number;
  title?: string;
  description?: string | null;
  sla_days?: number | null;
  completion_policy?: CompletionPolicy;
  entry_conditions?: any | null;
  exit_conditions?: any | null;
};

export type ProcessActivityTemplate = {
  id: string;
  step_template_id: string;
  title: string;
  description: string | null;
  is_required: boolean;
  activity_type: ActivityType;
  assignee_type: string | null;
  assignee_role: string | null;
  assignee_user_id: string | null;
  assignee_expression: string | null;
  fallback_assignee_role: string | null;
  visibility_scope: string;
  produces_deliverable_code: string | null;
  checklist_config: any | null;
  cost_activity_config: CostActivityConfig | null;
  assignee_activity_config: AssigneeActivityConfig | null;
  activity_ui_config: ActivityUIConfig | null;
  required_attachments: any | null;
  required_text_fields: any | null;
  sla_hours: number | null;
  created_at: string;
  updated_at: string;
};

export type CostActivityConfig = {
  cost_source?: "supplier_product" | "internal_labor" | "operational_fixed";
  supplier_product_id?: string | null;
  quantity_formula?: string | null;
  is_mandatory?: boolean;
  allow_override?: boolean;
  affect_inventory?: boolean;
  affect_margin?: boolean;
};

/** Config for activity_type = assign_responsible: who can be selected as responsible */
export type AssigneeActivityConfig = {
  assignee_source: "client_ambassador" | "specific_ambassador" | "internal_by_role";
  /** When assignee_source = specific_ambassador: org id of the ambassador */
  ambassador_org_id?: string | null;
  /** When assignee_source = internal_by_role: role id or role name (custom role) */
  role_id?: string | null;
};

/** Field type for activity UI */
export type ActivityUIFieldType = "text" | "textarea" | "number" | "select" | "checkbox" | "date" | "email";

/** Single field in the activity user interface */
export type ActivityUIField = {
  id: string;
  label: string;
  type: ActivityUIFieldType;
  required?: boolean;
  placeholder?: string | null;
  /** For type = select: list of options */
  choices?: Array<{ value: string; label: string }> | null;
  /** Default value (text, number, boolean, or choice value for select) */
  default_value?: string | number | boolean | null;
  /** For type = select: "dropdown" | "radio" */
  presentation?: "dropdown" | "radio";
};

/** Conditional rule: when source field matches, apply action to target field */
export type ActivityUIConditionalRule = {
  id: string;
  source_field_id: string;
  operator: "equals" | "not_equals" | "contains" | "empty";
  value?: string | number | boolean | null;
  target_field_id: string;
  action: "show" | "hide" | "require" | "optional";
};

/** System action triggered by a conditional rule or button */
export type ActivityUIAction = {
  type: "add_operational_cost" | "send_email" | "consume_inventory";
  params: Record<string, unknown>; // e.g. template_code, supplier_product_id, quantity_formula
};

/** Conditional rule that triggers system actions when field value matches */
export type ActivityUIActionRule = {
  id: string;
  source_field_id: string;
  operator: "equals" | "not_equals" | "contains" | "empty";
  value?: string | number | boolean | null;
  actions: ActivityUIAction[];
};

/** Visibility condition for a button (when to show it) */
export type ActivityUIButtonVisibility = {
  source_field_id: string;
  operator: "equals" | "not_equals" | "contains" | "empty";
  value?: string | number | boolean | null;
};

/** Context binding: how action params resolve (fixed vs task context) */
export type ActivityUIContextBinding = "fixed" | "task_product" | "task_invoice_client";

/** Button in the activity UI: label + action + optional visibility and context */
export type ActivityUIButton = {
  id: string;
  label: string;
  action_type: ActivityUIAction["type"];
  action_params: Record<string, unknown>;
  visibility_condition?: ActivityUIButtonVisibility | null;
  context_binding?: ActivityUIContextBinding | null;
};

/** Config for "Interface utilisateur de l'activité": fields, visibility rules, action rules, buttons */
export type ActivityUIConfig = {
  fields?: ActivityUIField[];
  conditional_rules?: ActivityUIConditionalRule[];
  action_rules?: ActivityUIActionRule[];
  buttons?: ActivityUIButton[];
};

export type ProcessActivityTemplateCreate = {
  title: string;
  description?: string | null;
  is_required?: boolean;
  activity_type?: ActivityType;
  assignee_type?: string | null;
  assignee_role?: string | null;
  assignee_user_id?: string | null;
  assignee_expression?: string | null;
  fallback_assignee_role?: string | null;
  visibility_scope?: string;
  produces_deliverable_code?: string | null;
  checklist_config?: any | null;
  cost_activity_config?: CostActivityConfig | null;
  assignee_activity_config?: AssigneeActivityConfig | null;
  activity_ui_config?: ActivityUIConfig | null;
  required_attachments?: any[] | null;
  required_text_fields?: any[] | null;
  sla_hours?: number | null;
};

export type ProcessActivityTemplateUpdate = {
  title?: string;
  description?: string | null;
  is_required?: boolean;
  activity_type?: ActivityType;
  assignee_type?: string | null;
  assignee_role?: string | null;
  assignee_user_id?: string | null;
  assignee_expression?: string | null;
  fallback_assignee_role?: string | null;
  visibility_scope?: string;
  produces_deliverable_code?: string | null;
  checklist_config?: any | null;
  cost_activity_config?: CostActivityConfig | null;
  assignee_activity_config?: AssigneeActivityConfig | null;
  activity_ui_config?: ActivityUIConfig | null;
  required_attachments?: any[] | null;
  required_text_fields?: any[] | null;
  sla_hours?: number | null;
};

export type ProcessTriggerTemplate = {
  id: string;
  step_template_id: string | null;
  event_type: TriggerEventType;
  action_type: TriggerActionType;
  action_payload: any | null;
  is_enabled: boolean;
  run_policy: string | null;
  debounce_seconds: number | null;
  conditions: any | null;
  created_at: string;
  updated_at: string;
};

export type ProcessTriggerTemplateCreate = {
  step_template_id?: string | null;
  event_type: TriggerEventType;
  action_type: TriggerActionType;
  action_payload?: any | null;
  is_enabled?: boolean;
  run_policy?: string | null;
  debounce_seconds?: number | null;
  conditions?: any | null;
};

export type ProcessTriggerTemplateUpdate = {
  step_template_id?: string | null;
  event_type?: TriggerEventType;
  action_type?: TriggerActionType;
  action_payload?: any | null;
  is_enabled?: boolean;
  run_policy?: string | null;
  debounce_seconds?: number | null;
  conditions?: any | null;
};

export type PagedProcessTemplates = {
  items: ProcessTemplate[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  };
};

export async function listProcessTemplates(params: {
  ops_family?: string;
  status?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedProcessTemplates> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params.ops_family) queryParams.append("ops_family", params.ops_family);
  if (params.status) queryParams.append("status", params.status);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/admin/process-templates?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load process templates");
  }
  return response.json();
}

export async function getProcessTemplate(templateId: string, includeDetails: boolean = true): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (includeDetails) {
    queryParams.append("include_details", "true");
  }
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load process template");
  }
  return response.json();
}

export type OpsFamilyCategory = { id: string; name: string };

export async function listOpsFamilyCategories(): Promise<OpsFamilyCategory[]> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/ops-families`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load process categories");
  }
  return response.json();
}

export async function createOpsFamilyCategory(payload: { name: string }): Promise<OpsFamilyCategory> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/ops-families`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to create category");
  }
  return response.json();
}

export async function createProcessTemplate(payload: ProcessTemplateCreate): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to create process template");
  }
  return response.json();
}

export async function updateProcessTemplate(
  templateId: string,
  payload: ProcessTemplateUpdate
): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update process template");
  }
  return response.json();
}

export async function addStepToDraft(
  templateId: string,
  payload: ProcessStepTemplateCreate
): Promise<ProcessStepTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}/draft/steps`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to add step");
  }
  return response.json();
}

export async function updateStep(stepId: string, payload: ProcessStepTemplateUpdate): Promise<ProcessStepTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/steps/${stepId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update step");
  }
  return response.json();
}

export async function deleteStep(stepId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/steps/${stepId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to delete step");
  }
}

export async function addActivityToStep(
  stepId: string,
  payload: ProcessActivityTemplateCreate
): Promise<ProcessActivityTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/steps/${stepId}/activities`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to add activity");
  }
  return response.json();
}

export async function updateActivity(
  activityId: string,
  payload: ProcessActivityTemplateUpdate
): Promise<ProcessActivityTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/activities/${activityId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update activity");
  }
  return response.json();
}

export async function deleteActivity(activityId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/activities/${activityId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to delete activity");
  }
}

export async function addTriggerToStep(
  stepId: string,
  payload: ProcessTriggerTemplateCreate
): Promise<ProcessTriggerTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/steps/${stepId}/triggers`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to add trigger");
  }
  return response.json();
}

export async function updateTrigger(
  triggerId: string,
  payload: ProcessTriggerTemplateUpdate
): Promise<ProcessTriggerTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/triggers/${triggerId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to update trigger");
  }
  return response.json();
}

export async function deleteTrigger(triggerId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/triggers/${triggerId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to delete trigger");
  }
}

export async function publishProcessTemplate(
  templateId: string,
  versionNumber?: number
): Promise<ProcessVersion> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}/publish`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({ version_number: versionNumber }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to publish process template");
  }
  return response.json();
}

// New endpoints
export type ProcessTemplateCloneRequest = {
  name?: string | null;
};

export type ProcessTemplateFullDetail = ProcessTemplate & {
  current_version?: {
    id: string;
    steps: Array<ProcessStepTemplate & {
      activities: ProcessActivityTemplate[];
      triggers: ProcessTriggerTemplate[];
    }>;
  } | null;
};

export type ValidationError = {
  field?: string | null;
  message: string;
  severity: "error" | "warning";
};

export type ProcessTemplateValidationResult = {
  is_valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
};

export type BreakingChange = {
  type: string;
  description: string;
  affected_count: number;
};

export type ProcessTemplatePublishDryRun = {
  affected_tasks_count: number;
  breaking_changes: BreakingChange[];
  migration_notes: string[];
};

export type BreakingChange = {
  type: string;
  description: string;
  affected_count: number | null;
};

export type ProcessTemplatePublishDryRun = {
  affected_tasks_count: number;
  breaking_changes: BreakingChange[];
  migration_notes: string[];
};

export async function cloneProcessTemplate(
  templateId: string,
  payload: ProcessTemplateCloneRequest
): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}/clone`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to clone process template");
  }
  return response.json();
}

export type ProcessTemplateDeleteResponse = {
  archived: boolean;
  deleted: boolean;
  template: ProcessTemplate | null;
};

export async function deleteProcessTemplate(
  templateId: string
): Promise<ProcessTemplateDeleteResponse> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Échec de la suppression du processus");
  }
  return response.json();
}

export async function archiveProcessTemplate(templateId: string): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}/archive`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to archive process template");
  }
  return response.json();
}

export async function activateProcessTemplate(
  templateId: string,
  activate: boolean = true
): Promise<ProcessTemplate> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  queryParams.append("activate", activate.toString());
  const response = await fetch(
    `${API_BASE}/admin/process-templates/${templateId}/activate?${queryParams.toString()}`,
    {
      method: "POST",
      headers: defaultHeaders(orgId),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to activate/deactivate process template");
  }
  return response.json();
}

export async function getDraftFull(templateId: string): Promise<ProcessTemplateFullDetail> {
  console.log("🔵 getDraftFull called with templateId:", templateId);
  try {
    const orgId = await getAdminOrgId();
    console.log("✅ getAdminOrgId returned:", orgId);
    if (!orgId) {
      throw new Error("Organization ID is missing");
    }
    const url = `${API_BASE}/admin/process-templates/${templateId}/draft/full`;
    console.log("📞 Fetching URL:", url);
    const response = await fetch(url, {
      headers: defaultHeaders(orgId),
    });
    console.log("📥 Response status:", response.status, response.statusText);
    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ getDraftFull failed:", errorText);
      throw new Error(errorText || "Failed to load draft full");
    }
    const data = await response.json();
    console.log("✅ getDraftFull success:", data);
    return data;
  } catch (error: any) {
    console.error("❌ getDraftFull error:", error);
    throw error;
  }
}

export async function validateDraft(templateId: string): Promise<ProcessTemplateValidationResult> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/${templateId}/draft/validate`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to validate draft");
  }
  return response.json();
}

export async function publishDryRun(templateId: string): Promise<ProcessTemplatePublishDryRun> {
  const orgId = await getAdminOrgId();
  const response = await fetch(
    `${API_BASE}/admin/process-templates/${templateId}/draft/publish-dry-run`,
    {
      method: "POST",
      headers: defaultHeaders(orgId),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to run publish dry-run");
  }
  return response.json();
}

export async function reorderStep(stepId: string, newOrderIndex: number): Promise<ProcessStepTemplate> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/process-templates/steps/${stepId}/reorder`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({ new_order_index: newOrderIndex }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to reorder step");
  }
  return response.json();
}
